import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate, useParams } from "react-router-dom";
import { BookmarkSimple, CheckCircle, Cube, Drop } from "@phosphor-icons/react";
import { PortalShell } from "@/components/PortalShell";
import { PortalLightbox } from "@/components/PortalLightbox";
import { PortalReserveDialog } from "@/components/PortalReserveDialog";
import { SinkViewer } from "@/components/SinkViewer";
import { portalApi, portalAsset, idFromSlug } from "@/lib/portalApi";

const plainText = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
const hasRich = (html) => /<(p|br|ul|ol|li|h1|h2|h3|strong|b|em|i|u|a|blockquote)\b/i.test(html || "");

export default function PortalSinkItem() {
  const { slug } = useParams();
  const id = idFromSlug(slug);
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [pricing, setPricing] = useState({ show: false });
  const [loading, setLoading] = useState(true);
  const [reserve, setReserve] = useState(false);
  const [light, setLight] = useState(-1);
  const [view3d, setView3d] = useState(false);
  const [activePhoto, setActivePhoto] = useState(null);

  useEffect(() => {
    setLoading(true);
    portalApi
      .get(`/sinks/${id}`)
      .then(({ data }) => {
        setItem(data.item);
        setPricing(data.pricing || { show: false });
      })
      .catch(() => setItem(null))
      .finally(() => setLoading(false));
  }, [id]);

  const shots = useMemo(
    () =>
      (item?.photos || []).map((p, n) => ({
        path: p.path,
        thumb: p.thumb,
        title: item.model,
        sub: `${item.sink_type}${item.size ? ` · ${item.size}` : ""}${
          (item.photos || []).length > 1
            ? ` · photo ${n + 1} of ${item.photos.length}`
            : ""
        }`,
      })),
    [item],
  );

  const viewerPhotos = useMemo(() => {
    const arr = (item?.photos || []).map((p) => ({
      src: portalAsset(`/inventory/photo/${p.path}?w=1600`),
      angle: p.angle || "",
    }));
    if (!arr.length && item?.photo)
      arr.push({ src: portalAsset(`/inventory/photo/${item.photo}?w=1600`), angle: "" });
    return arr;
  }, [item]);

  if (loading)
    return (
      <PortalShell back="/portal/sinks">
        <div className="py-24 text-center text-sm text-slate-500" data-testid="portal-sink-loading">
          Loading…
        </div>
      </PortalShell>
    );

  if (!item)
    return (
      <PortalShell back="/portal/sinks">
        <div className="py-24 text-center" data-testid="portal-sink-missing">
          <p className="text-sm text-slate-500">This sink is no longer listed.</p>
          <button
            type="button"
            onClick={() => navigate("/portal/sinks")}
            className="mt-3 text-sm font-semibold text-[#c39e6d] underline"
          >
            Back to sinks
          </button>
        </div>
      </PortalShell>
    );

  const stock = item.qty > 0 ? `${item.qty} in stock` : "Supplier Stock";
  const specs = [
    ["Brand", item.brand],
    ["Type", item.sink_type],
    ["Bowls", item.bowls],
    ["Material", item.material],
    item.gauge ? ["Gauge", `${item.gauge} ga`] : null,
    ["Availability", item.special_order ? "Supplier Stock" : "Stock sink"],
    ["Size", item.size],
    ["In stock", stock],
    pricing.show && item.price != null
      ? ["Your price", `$${item.price.toFixed(2)}`]
      : null,
  ]
    .filter(Boolean)
    .filter(([, v]) => v);

  return (
    <PortalShell back="/portal/sinks">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2" data-testid="portal-sink-item">
        <div>
          <div className="relative aspect-square overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
            {pricing.show && item.price != null && (
              <span
                data-testid="portal-sink-item-price-chip"
                className="font-mono-data absolute right-4 top-4 z-10 rounded-full bg-[#c39e6d] px-4 py-1.5 text-sm font-bold tracking-tight text-slate-900 shadow-sm"
              >
                ${item.price.toFixed(2)}
              </span>
            )}
            {item.photo ? (
              <button
                type="button"
                data-testid="portal-sink-item-photo"
                onClick={() => setLight(0)}
                className="block h-full w-full cursor-zoom-in"
              >
                <img
                  src={portalAsset(`/inventory/photo/${activePhoto || item.photo}?w=1200`)}
                  alt={item.model}
                  className="h-full w-full object-contain p-5"
                />
              </button>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <Drop size={40} weight="thin" className="text-slate-300" />
              </div>
            )}
          </div>
          {(item.photos || []).length > 1 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {item.photos.map((p, n) => (
                <button
                  key={p.path}
                  type="button"
                  onClick={() => setLight(n)}
                  data-testid={`portal-sink-thumb-${n}`}
                  className="relative h-16 w-16 overflow-hidden rounded-lg border border-slate-200 transition-colors duration-150 hover:border-[#c39e6d]"
                >
                  <img
                    src={portalAsset(`/inventory/photo/${p.thumb}?w=160`)}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                  {p.angle && (
                    <span className="absolute inset-x-0 bottom-0 bg-slate-900/75 py-0.5 text-center text-[8px] font-bold uppercase tracking-widest text-white">
                      {p.angle}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {(item.finishes || []).length > 0 && (
            <div className="mt-5" data-testid="portal-sink-finishes">
              <div className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Finishes
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {item.finishes.map((fin, n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`portal-sink-finish-${n}`}
                    onClick={() => fin.photo && setActivePhoto(fin.photo)}
                    title={fin.name}
                    className={`flex items-center gap-2 rounded-full border py-1 pl-1 pr-3 transition-colors duration-150 ${
                      activePhoto && activePhoto === fin.photo
                        ? "border-[#c39e6d] bg-[#c39e6d]/10"
                        : "border-slate-200 hover:border-[#c39e6d]"
                    }`}
                  >
                    {fin.thumb ? (
                      <img
                        src={portalAsset(`/inventory/photo/${fin.thumb}?w=80`)}
                        alt=""
                        className="h-7 w-7 rounded-full object-cover"
                      />
                    ) : (
                      <span className="h-7 w-7 rounded-full bg-slate-200" />
                    )}
                    <span className="text-xs font-semibold text-slate-700">
                      {fin.name || "Finish"}
                    </span>
                    {fin.sku && (
                      <span className="font-mono-data text-[10px] text-slate-400">
                        {fin.sku}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            {item.sink_type && (
              <span className="rounded-md bg-slate-900 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                {item.sink_type}
              </span>
            )}
            {item.material && (
              <span className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                {item.material}
              </span>
            )}
          </div>
          <div className="mt-3 font-mono-data text-[10px] uppercase tracking-[0.22em] text-slate-500">
            Sink{item.model ? ` · ${item.model}` : item.code ? ` · ${item.code}` : ""}
          </div>
          <h1
            className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
            data-testid="portal-sink-name"
          >
            {plainText(item.description) || item.model || "Sink"}
          </h1>

          <dl className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            {specs.map(([k, v]) => (
              <div key={k} className="bg-white px-4 py-3">
                <dt className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  {k}
                </dt>
                <dd className="mt-1 text-sm text-slate-900">{v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-6 flex flex-wrap gap-3">
            {item.requested ? (
              <span
                className="inline-flex h-12 items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-6 text-[11px] font-bold uppercase tracking-widest text-emerald-600"
                data-testid="portal-sink-requested"
              >
                <CheckCircle size={15} weight="bold" /> Requested — we&apos;ll be in touch
              </span>
            ) : (
              <button
                type="button"
                data-testid="portal-sink-reserve"
                onClick={() => setReserve(true)}
                className="inline-flex h-12 items-center gap-2 rounded-full bg-[#c39e6d] px-7 text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-[#a9855a]"
              >
                <BookmarkSimple size={15} weight="bold" /> Ask us to hold this
              </button>
            )}
            {item.photo && (
              <button
                type="button"
                data-testid="portal-sink-3d"
                onClick={() => setView3d(true)}
                className="inline-flex h-12 items-center gap-2 rounded-full border border-slate-300 px-6 text-[11px] font-bold uppercase tracking-widest text-slate-700 transition-colors duration-150 hover:border-slate-400 hover:text-slate-900"
              >
                <Cube size={15} weight="bold" /> 3D view
              </button>
            )}
          </div>
          {item.description && hasRich(item.description) && (
            <div
              data-testid="portal-sink-description"
              className="rte-prose mt-4 text-sm leading-relaxed text-slate-600"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(item.description),
              }}
            />
          )}
        </div>
      </div>

      {view3d && (
        <SinkViewer
          photos={viewerPhotos}
          title={item.model || "Sink"}
          onClose={() => setView3d(false)}
        />
      )}
      {reserve && (
        <PortalReserveDialog
          item={{ id: item.id, kind: "Sink", label: item.model }}
          onClose={() => setReserve(false)}
          onDone={() => {
            setReserve(false);
            setItem((it) => ({ ...it, requested: true }));
          }}
        />
      )}
      {light >= 0 && shots.length > 0 && (
        <PortalLightbox
          shots={shots}
          index={light}
          srcOf={(path) => portalAsset(`/inventory/photo/${path}`)}
          onClose={() => setLight(-1)}
        />
      )}
    </PortalShell>
  );
}
